import React from 'react';
import { useNotifications } from '../../context/NotificationsContext';

const CATEGORIES = [
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'BOT_RUN', label: 'Bot runs' },
  { value: 'BOT_SIGNAL', label: 'Bot signals' },
  { value: 'INDICATOR_ALERT', label: 'Indicator alerts' },
  { value: 'BACKTEST', label: 'Backtests' },
  { value: 'RISK', label: 'Risk' },
  { value: 'SYSTEM', label: 'System' },
  { value: 'BILLING', label: 'Billing' },
];
const SEVERITIES = ['INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL'];

function PreferenceSwitch({ checked, label, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`${label} web notifications`}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border',
        'transition-colors duration-200 ease-in-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-backgroundSurface',
        checked
          ? 'border-primary bg-primary'
          : 'border-borderDefault bg-backgroundBody hover:border-primary/50',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'inline-block h-5 w-5 rounded-full bg-white shadow-sm',
          'transition-transform duration-200 ease-in-out',
          checked ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}

export default function NotificationsPreferences() {
  const { preferences, upsertPreference } = useNotifications();

  const getPref = (category) => (
    preferences?.find((preference) => (
      preference.channel === 'WEB' && preference.category === category
    )) || {
      channel: 'WEB',
      category,
      enabled: true,
      min_severity: 'INFO',
    }
  );

  const toggleEnabled = async (category, enabled) => {
    await upsertPreference({ ...getPref(category), enabled });
  };
  const changeSeverity = async (category, min_severity) => {
    await upsertPreference({ ...getPref(category), min_severity });
  };

  return (
    <div className="space-y-3">
      {CATEGORIES.map(({ value: category, label }) => {
        const pref = getPref(category);
        const severityId = `notification-severity-${category.toLowerCase()}`;

        return (
          <div
            key={category}
            className="min-w-0 rounded-lg border border-borderDefault bg-backgroundBody/30 p-3 sm:p-4"
          >
            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(12rem,auto)] md:gap-x-5">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-textPrimary sm:text-base">
                  {label}
                </div>
                <div className="mt-0.5 text-xs text-textSecondary">Web notifications</div>
              </div>

              <div className="flex items-center gap-2">
                <span className="w-6 text-right text-xs font-medium text-textSecondary" aria-hidden="true">
                  {pref.enabled ? 'On' : 'Off'}
                </span>
                <PreferenceSwitch
                  checked={!!pref.enabled}
                  label={label}
                  onChange={(enabled) => toggleEnabled(category, enabled)}
                />
              </div>

              <label
                htmlFor={severityId}
                className="col-span-2 flex min-w-0 items-center justify-between gap-3 border-t border-borderDefault pt-3 md:col-span-1 md:border-0 md:pt-0"
              >
                <span className="text-xs text-textSecondary sm:text-sm">Minimum severity</span>
                <select
                  id={severityId}
                  aria-label={`${label} minimum severity`}
                  className="w-28 max-w-full rounded-md border border-borderDefault bg-backgroundInput px-2.5 py-2 text-sm text-textPrimary outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30 sm:w-32"
                  value={pref.min_severity}
                  onChange={(event) => changeSeverity(category, event.target.value)}
                >
                  {SEVERITIES.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </label>
            </div>
          </div>
        );
      })}

      <div className="rounded-lg border border-dashed border-borderDefault p-3 text-sm text-textSecondary sm:p-4">
        Email preferences coming soon.
      </div>
    </div>
  );
}
